export * from "./types";
export * from "./queries";

import { graphqlRequest } from "@/services/deployment/client";
import type {
  BackendObservabilityEvent,
  BackendSchemaResponse,
  ObservabilityData,
  Period,
  AvailableSection,
  AgentSummary,
  AgentRoute,
  AgentUser,
  AgentTimePoint,
  AgentRun,
  ToolSummary,
  ToolRow,
  ToolServer,
  RagSummary,
} from "./types";
import { OBSERVABILITY_SCHEMA_QUERY } from "./queries";

export function isSuccessEvent(event: BackendObservabilityEvent): boolean {
  const status = String(event.status ?? "").toLowerCase();
  return event.action !== "run_failed" && !event.error && !["failed", "error", "timeout"].includes(status);
}

export function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
}

export function average(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function round(value: number, digits = 2): number {
  return Number(value.toFixed(digits));
}

export function dayBucket(value: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : "unknown";
}

// Per-user consumption breakdown (cost + tokens), highest token spend first.
// Built from the latest event per run so a run is counted once.
export function buildAgentUsers(events: BackendObservabilityEvent[]): AgentUser[] {
  const map = new Map<string, BackendObservabilityEvent[]>();
  for (const event of events) {
    const key = event.userId || "unknown";
    map.set(key, [...(map.get(key) ?? []), event]);
  }
  return Array.from(map.entries())
    .map(([user_id, userEvents]) => ({
      user_id,
      runs: userEvents.length,
      input_tokens: userEvents.reduce((sum, event) => sum + Number(event.inputTokens ?? 0), 0),
      output_tokens: userEvents.reduce((sum, event) => sum + Number(event.outputTokens ?? 0), 0),
      total_tokens: userEvents.reduce(
        (sum, event) => sum + Number(event.inputTokens ?? 0) + Number(event.outputTokens ?? 0),
        0,
      ),
      cost: round(userEvents.reduce((sum, event) => sum + Number(event.costUsd ?? 0), 0), 4),
      pass_rate: round((userEvents.filter(isSuccessEvent).length / Math.max(userEvents.length, 1)) * 100),
    }))
    .sort((a, b) => b.total_tokens - a.total_tokens);
}

// Map a raw service name to one of the product surfaces the UI segregates
// by. Keep in sync with each service's product_observability `service` value.
export function productOfService(
  service: string,
): "agents" | "migrations" | "dev-agent" | "multi-agent" | "testing" | "salesforce-releases" | "other" {
  if (service === "agents") return "agents";
  if (service === "playground") return "migrations";
  if (service === "agentic_platform") return "dev-agent";
  if (service === "multi-step-agentic") return "multi-agent";
  if (service === "testing") return "testing";
  if (service === "release-scanner") return "salesforce-releases";
  return "other";
}

export const PRODUCT_LABELS: Record<string, string> = {
  all: "All Products",
  agents: "Agents",
  migrations: "Migrations",
  // "dev-agent": "Dev Agent",
  "multi-agent": "AI with Weave",
  testing: "Testing",
  "salesforce-releases": "Salesforce Releases",
};

export function buildObservabilityData(
  events: BackendObservabilityEvent[],
  orgId: string,
  period: Period,
): ObservabilityData {
  const runEvents = events.filter((event) => event.action.startsWith("run_"));
  const toolEvents = events.filter((event) => event.action === "tool_called");
  const runLatencies = runEvents.map((event) => event.latencyMs ?? 0).filter(Boolean);

  const routeMap = new Map<string, BackendObservabilityEvent[]>();
  for (const event of runEvents) {
    const route = `${event.service}${event.surface ? ` / ${event.surface}` : ""}`;
    routeMap.set(route, [...(routeMap.get(route) ?? []), event]);
  }

  const dayMap = new Map<string, BackendObservabilityEvent[]>();
  for (const event of runEvents) {
    const bucket = dayBucket(event.occurredAt);
    dayMap.set(bucket, [...(dayMap.get(bucket) ?? []), event]);
  }

  const latestByRun = new Map<string, BackendObservabilityEvent>();
  for (const event of runEvents) {
    const key = event.runId || event.id;
    const current = latestByRun.get(key);
    if (!current || String(event.occurredAt ?? "") > String(current.occurredAt ?? "")) latestByRun.set(key, event);
  }
  const latestRuns = Array.from(latestByRun.values());
  const runCount = latestRuns.length;
  const successfulLatestRuns = latestRuns.filter(isSuccessEvent).length;

  const agentsSection: AvailableSection<{
    summary: AgentSummary;
    routes: AgentRoute[];
    users: AgentUser[];
    time_series: AgentTimePoint[];
    recent_runs: AgentRun[];
  }> = {
    available: true,
    summary: {
      requests: runCount,
      pass_rate: runCount ? round((successfulLatestRuns / runCount) * 100) : 0,
      error_rate: runCount ? round(((runCount - successfulLatestRuns) / runCount) * 100) : 0,
      p95_latency_s: round(percentile(runLatencies, 95) / 1000),
      total_cost: round(runEvents.reduce((sum, event) => sum + Number(event.costUsd ?? 0), 0), 4),
      avg_iterations: round(average(runEvents.map((event) => Number(event.metadata?.iterations ?? 0)).filter(Boolean))),
      input_tokens: runEvents.reduce((sum, event) => sum + Number(event.inputTokens ?? 0), 0),
      output_tokens: runEvents.reduce((sum, event) => sum + Number(event.outputTokens ?? 0), 0),
      total_tokens: runEvents.reduce(
        (sum, event) => sum + Number(event.inputTokens ?? 0) + Number(event.outputTokens ?? 0),
        0,
      ),
    },
    routes: Array.from(routeMap.entries()).map(([route, routeEvents]) => ({
      route,
      turns: routeEvents.length,
      avg_latency_s: round(average(routeEvents.map((event) => event.latencyMs ?? 0).filter(Boolean)) / 1000),
      avg_cost: round(average(routeEvents.map((event) => Number(event.costUsd ?? 0)).filter(Boolean)), 4),
      total_cost: round(routeEvents.reduce((sum, event) => sum + Number(event.costUsd ?? 0), 0), 4),
      input_tokens: routeEvents.reduce((sum, event) => sum + Number(event.inputTokens ?? 0), 0),
      output_tokens: routeEvents.reduce((sum, event) => sum + Number(event.outputTokens ?? 0), 0),
      total_tokens: routeEvents.reduce(
        (sum, event) => sum + Number(event.inputTokens ?? 0) + Number(event.outputTokens ?? 0),
        0,
      ),
      pass_rate: round((routeEvents.filter(isSuccessEvent).length / Math.max(routeEvents.length, 1)) * 100),
    })),
    time_series: Array.from(dayMap.entries()).map(([bucket, bucketEvents]) => ({
      bucket,
      turns: bucketEvents.length,
      pass_rate: round((bucketEvents.filter(isSuccessEvent).length / Math.max(bucketEvents.length, 1)) * 100),
      avg_latency_s: round(average(bucketEvents.map((event) => event.latencyMs ?? 0).filter(Boolean)) / 1000),
      cost: round(bucketEvents.reduce((sum, event) => sum + Number(event.costUsd ?? 0), 0), 4),
    })),
    users: buildAgentUsers(latestRuns),
    // ALL runs (capped only by the events query limit) so the CSV export is the full
    // per-run report; the UI table slices this to a recent window for display.
    recent_runs: latestRuns.map((event) => ({
      run_id: event.runId || event.id,
      route: `${event.service}${event.surface ? ` / ${event.surface}` : ""}`,
      // Human-readable label so the user recognizes the run: the actual
      // question (agent / multi-agent / dev-agent) or the name (migration /
      // playbook / agent). Falls back through the known metadata keys, then to
      // the raw action only as a last resort.
      intent: String(
        event.metadata?.intent ||
          event.metadata?.question ||
          event.metadata?.user_intent ||
          event.metadata?.name ||
          event.metadata?.agent_name ||
          event.metadata?.playbook_name ||
          event.metadata?.title ||
          event.metadata?.plan_id ||
          event.metadata?.document_id ||
          event.action,
      ),
      iterations: Number(event.metadata?.iterations ?? 0),
      tool_calls: Number(event.metadata?.tool_call_count ?? event.metadata?.llm_calls ?? 0),
      cost: Number(event.costUsd ?? 0),
      input_tokens: Number(event.inputTokens ?? 0),
      output_tokens: Number(event.outputTokens ?? 0),
      total_tokens: Number(event.inputTokens ?? 0) + Number(event.outputTokens ?? 0),
      latency_s: round(Number(event.latencyMs ?? 0) / 1000),
      final_pass: isSuccessEvent(event),
      has_error: Boolean(event.error || event.action === "run_failed"),
      trace_id: event.traceId,
      user_id: event.userId,
      created_at: event.occurredAt,
    })),
  };

  const toolMap = new Map<string, BackendObservabilityEvent[]>();
  const serverMap = new Map<string, BackendObservabilityEvent[]>();
  for (const event of toolEvents) {
    const tool = String(event.metadata?.tool || "unknown");
    const server = String(event.metadata?.server || event.service);
    toolMap.set(tool, [...(toolMap.get(tool) ?? []), event]);
    serverMap.set(server, [...(serverMap.get(server) ?? []), event]);
  }

  const toolsSection: AvailableSection<{
    summary: ToolSummary;
    tools: ToolRow[];
    servers: ToolServer[];
  }> = {
    available: true,
    summary: {
      calls: toolEvents.length,
      success_rate: toolEvents.length ? round((toolEvents.filter(isSuccessEvent).length / toolEvents.length) * 100) : 0,
      p95_duration_s: round(percentile(toolEvents.map((event) => event.latencyMs ?? 0).filter(Boolean), 95) / 1000),
      result_tokens: toolEvents.reduce((sum, event) => sum + Number(event.outputTokens ?? 0), 0),
    },
    tools: Array.from(toolMap.entries()).map(([toolName, rows]) => ({
      tool_name: toolName,
      mcp_server: String(rows[0]?.metadata?.server || rows[0]?.service || "unknown"),
      calls: rows.length,
      success_rate: round((rows.filter(isSuccessEvent).length / Math.max(rows.length, 1)) * 100),
      avg_duration_s: round(average(rows.map((event) => event.latencyMs ?? 0).filter(Boolean)) / 1000),
      p95_duration_s: round(percentile(rows.map((event) => event.latencyMs ?? 0).filter(Boolean), 95) / 1000),
      result_tokens: rows.reduce((sum, event) => sum + Number(event.outputTokens ?? 0), 0),
      last_seen: rows[0]?.occurredAt ?? null,
    })),
    servers: Array.from(serverMap.entries()).map(([server, rows]) => ({
      mcp_server: server,
      calls: rows.length,
      success_rate: round((rows.filter(isSuccessEvent).length / Math.max(rows.length, 1)) * 100),
      p95_duration_s: round(percentile(rows.map((event) => event.latencyMs ?? 0).filter(Boolean), 95) / 1000),
    })),
  };

  const serviceRows = latestRuns.slice(0, 50);
  const serviceStatusMap = new Map<string, number>();
  for (const event of runEvents) {
    const key = `${event.service}:${event.status || event.action}`;
    serviceStatusMap.set(key, (serviceStatusMap.get(key) ?? 0) + 1);
  }

  const ragSummary: RagSummary = {
    runs: runCount,
    success_rate: runCount ? round((successfulLatestRuns / runCount) * 100) : 0,
    avg_duration_s: round(average(runLatencies) / 1000),
    nodes_created: events.length,
    relationships_created: toolEvents.length,
  };

  return {
    period,
    org_id: orgId,
    events,
    agents: agentsSection,
    tools: toolsSection,
    rag: {
      available: true,
      summary: ragSummary,
      pipeline_runs: serviceRows.map((event) => ({
        job_id: event.runId || event.id,
        status: event.status || event.action,
        duration_s: round(Number(event.latencyMs ?? 0) / 1000),
        enrichment_version_id: `${event.service}${event.surface ? ` / ${event.surface}` : ""}`,
        nodes_created: Number(event.inputTokens ?? 0),
        relationships_created: Number(event.outputTokens ?? 0),
        completed_at: event.occurredAt,
      })),
      status_breakdown: Array.from(serviceStatusMap.entries()).map(([status, count]) => ({ status, count })),
      time_series: Array.from(dayMap.entries()).map(([bucket, bucketEvents]) => ({
        bucket,
        runs: bucketEvents.length,
        avg_duration_s: round(average(bucketEvents.map((event) => event.latencyMs ?? 0).filter(Boolean)) / 1000),
        nodes_created: bucketEvents.length,
        relationships_created: bucketEvents.filter((event) => event.action === "tool_called").length,
      })),
      enrichment_versions: [],
    },
  };
}

export function isObservabilityContractMissing(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message.includes('Cannot query field "observabilityEvents"') ||
    message.includes("Cannot query field 'observabilityEvents'")
  );
}

export async function backendHasObservabilityContract(): Promise<boolean> {
  const response = await graphqlRequest<BackendSchemaResponse>(OBSERVABILITY_SCHEMA_QUERY);
  return Boolean(response.__type?.fields?.some((field) => field.name === "observabilityEvents"));
}
